import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  HomeVisitSettings,
  HomeVisitSettingsInput,
  LabAppBooking,
  LabAppBookingsQuery,
  LabPackage,
  LabPackageInput,
  LabPackageUpdate,
} from './types';

const packagesKey = ['lab-packages'] as const;
const settingsKey = ['lab-packages', 'settings'] as const;
const bookingsKey = ['lab-app-bookings'] as const;

/** GET /lab-packages?all: packages in app order (all = with switched-off ones). */
export function useLabPackages(all: boolean) {
  return useQuery({
    queryKey: [...packagesKey, 'list', all],
    queryFn: async ({ signal }) =>
      (
        await api.get<LabPackage[]>('/lab-packages', {
          signal,
          query: all ? { all: 'true' } : undefined,
        })
      ).data,
  });
}

/** POST /lab-packages and PATCH /lab-packages/{id} {version, …}. */
export function useLabPackageActions() {
  const qc = useQueryClient();
  const onSettled = () => qc.invalidateQueries({ queryKey: packagesKey });
  return {
    create: useMutation({
      mutationFn: async (body: LabPackageInput) =>
        (await api.post<LabPackage>('/lab-packages', body)).data,
      onSettled,
    }),
    update: useMutation({
      mutationFn: async ({
        id,
        body,
      }: {
        id: string;
        body: LabPackageUpdate;
      }) => (await api.patch<LabPackage>(`/lab-packages/${id}`, body)).data,
      onSettled,
    }),
  };
}

/** GET /lab-packages/settings: the home-visit settings. */
export function useHomeVisitSettings() {
  return useQuery({
    queryKey: settingsKey,
    queryFn: async ({ signal }) =>
      (
        await api.get<HomeVisitSettings>('/lab-packages/settings', {
          signal,
        })
      ).data,
  });
}

/** PUT /lab-packages/settings (fields sent replace). */
export function useSaveHomeVisitSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: HomeVisitSettingsInput) =>
      (await api.put<HomeVisitSettings>('/lab-packages/settings', body)).data,
    onSuccess: (saved) => qc.setQueryData(settingsKey, saved),
  });
}

/** GET /lab/app-bookings?scope&mode at the selected branch (lab desk). */
export function useLabAppBookings(q: LabAppBookingsQuery) {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: [...bookingsKey, branch, q.scope, q.mode],
    queryFn: async ({ signal }) =>
      (
        await api.get<LabAppBooking[]>('/lab/app-bookings', {
          signal,
          query: { scope: q.scope, mode: q.mode || undefined },
        })
      ).data,
    enabled: branch !== 'all',
    refetchInterval: 30_000,
  });
}

/** POST /lab/app-bookings/{id}/cancel {version, reason} (lab_orders:process). */
export function useCancelLabAppBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; version: number; reason: string }) =>
      (
        await api.post<LabAppBooking>(`/lab/app-bookings/${v.id}/cancel`, {
          version: v.version,
          reason: v.reason,
        })
      ).data,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: bookingsKey }),
        qc.invalidateQueries({ queryKey: ['lab-orders'] }),
      ]),
  });
}
